import React from 'react';
import { X } from 'lucide-react';
import { Agent } from '../../types/agent';
import { agents } from '../../constants/agents';
import { AnimatedAvatar } from './AnimatedAvatar';
import { useAgent } from '../../contexts/AgentContext';

interface AgentSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AgentSelectionDialog: React.FC<AgentSelectionDialogProps> = ({
  isOpen,
  onClose
}) => {
  const { mainAgent, selectedAgents, addAgent } = useAgent();
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Select & add an Agent</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-500 dark:text-gray-400"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className={`
                border rounded-xl p-5 cursor-pointer transition-all duration-200 h-[140px] flex items-center
                ${agent.enabled 
                  ? 'hover:border-blue-500 dark:hover:border-blue-400' 
                  : 'opacity-50 cursor-not-allowed'}
                ${agent.id === mainAgent.id || selectedAgents.some(a => a.id === agent.id)
                  ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-200 dark:border-gray-700'}
              `}
              onClick={() => {
                if (agent.enabled) {
                  addAgent(agent);
                  onClose();
                }
              }}
            >
              <div className="flex items-center space-x-4 w-full">
                <AnimatedAvatar 
                  src={agent.avatar}
                  alt={`${agent.name} Logo`}
                  size="lg"
                  enabled={agent.enabled}
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg text-gray-900 dark:text-white truncate">{agent.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{agent.description}</p>
                  {!agent.enabled && (
                    <span className="inline-block mt-2 text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full">
                      Coming Soon
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
